/**
 * assets/collection-availability-filter.js
 * ---------------------------------------------------------------------------
 * Conteo y filtrado REALES de Availability (In Stock / Special Order) para
 * páginas de colección NATIVAS — Nivel 1 (sections/collection-hub.liquid) y
 * Nivel 3 (sections/main-collection.liquid), montado desde el sidebar
 * compartido snippets/collection-filters.liquid.
 *
 * Por qué existe: el facet nativo "Availability" de Shopify (collection.filters)
 * se basa en el booleano `available` ("¿sigue vendiendo?"), que no distingue
 * cantidad real de inventario de "continue selling con cantidad 0" — el mismo
 * defecto ya resuelto en Nivel 2 (páginas Hub, ver assets/hub-storefront-filters.js).
 * En Nivel 1/3 collection.filters SÍ funciona perfecto para todo lo demás
 * (precio, marca, orden, paginación), así que este módulo NO reconstruye el
 * sidebar ni reemplaza la paginación nativa por defecto — se queda dormido
 * hasta que el visitante toca una de las dos casillas de Availability, y
 * únicamente entonces toma control de la grilla + paginación, dejando
 * exactamente igual todo lo demás (los otros filtros nativos, orden, etc.
 * siguen recargando la página como siempre).
 *
 * Flujo:
 *   1) Al cargar: calcula el conteo real (in stock / special order) de TODA
 *      la colección — respetando los demás filtros activos — y parcha solo
 *      los dos <span data-availability-count-for> del sidebar SSR.
 *   2) Al marcar una casilla de Availability: se intercepta ANTES de que
 *      dispare el submit nativo (ver snippets/collection-filters.liquid, que
 *      excluye estas dos casillas del wiring genérico [data-filter-checkbox]).
 *      Se escanea la colección vía Storefront API en bloques de 250,
 *      filtrando client-side con el mismo criterio que ya usa la tarjeta de
 *      producto, acumulando hasta llenar una página — el HTML de la grilla y
 *      una paginación Previous/Next reemplazan al contenido nativo. La URL
 *      nunca cambia mientras Availability está activo.
 *   3) Al desmarcar: recarga completa de la página — nunca se agregó ningún
 *      parámetro de Availability a la URL, así que un reload simple ya
 *      revierte a SSR nativo puro, sin necesitar lógica de "deshacer" en JS.
 *
 * Deliberadamente NO comparte runtime con hub-storefront-filters.js (Nivel 2)
 * — algunos helpers puros están duplicados a propósito, para no arriesgar ese
 * módulo ya estable con un refactor de por medio.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Helpers puros — copiados verbatim de assets/hub-storefront-filters.js
   * (ese archivo no exporta nada desde su IIFE, así que se duplican aquí).
   * ------------------------------------------------------------------- */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    cents = (cents / 100.0).toFixed(precision);
    var parts = cents.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  function formatMoney(amount, format) {
    var cents = Math.round(parseFloat(amount || 0) * 100);
    format = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  // OJO: textContent -> innerHTML solo escapa & < > (asi lo define la
  // spec), NO las comillas. Como esto se interpola DENTRO de atributos
  // (src="...", href="...", alt="..."), un valor con comilla doble
  // —y altText lo controla quien carga el producto— se sale del atributo:
  // src="https://x/a" onerror="...". Se escapan tambien las comillas.
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function handleize(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Única fuente de verdad de "en stock real" en todo el sitio — la tarjeta
  // (snippets/product-item.liquid), el filtro de Nivel 2 y este módulo usan
  // EXACTAMENTE este mismo criterio, para que nunca puedan quedar en
  // desacuerdo entre sí.
  function productIsNotInStock(product) {
    // A pedido: los productos que piden cotización (Get A Quote) cuentan como Special Order siempre
    // (assets/quote-products.js, espejo de quote-product-check.liquid) — etiqueta y filtro coinciden.
    if (window.CSQuote && window.CSQuote.isQuote(product)) return true;
    var variant = product.variants.nodes[0];
    return !product.availableForSale || (variant && variant.currentlyNotInStock);
  }

  /* ---------------------------------------------------------------------
   * Cliente Storefront API — idéntico al de hub-storefront-filters.js.
   * ------------------------------------------------------------------- */
  function StorefrontClient(shopDomain, token, apiVersion) {
    this.endpoint = 'https://' + shopDomain + '/api/' + apiVersion + '/graphql.json';
    this.token = token;
  }

  StorefrontClient.prototype.query = function (query, variables) {
    return fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.token,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (json.errors && json.errors.length) {
          throw new Error(json.errors.map(function (e) { return e.message; }).join('; '));
        }
        // Productos publicados en el canal de la Storefront API pero NO en la tienda
        // online (onlineStoreUrl === null): no tienen página (404), así que no se
        // cuentan ni se muestran — antes inflaban los conteos de Availability y
        // salían como tarjetas con enlace roto.
        var coll = json.data && json.data.collection;
        if (coll && coll.products && coll.products.edges) {
          coll.products.edges = coll.products.edges.filter(function (e) {
            return !(e && e.node && e.node.onlineStoreUrl === null);
          });
        }
        return json.data;
      });
  };

  // Consulta liviana para el conteo real (Fase A) — solo lo mínimo para
  // aplicar productIsNotInStock, no toda la ficha del producto.
  var AVAILABILITY_COUNT_QUERY = [
    'query CollectionAvailabilityCount($handle: String!, $first: Int!, $after: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean, $filters: [ProductFilter!]) {',
    '  collection(handle: $handle) {',
    '    products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {',
    '      pageInfo { hasNextPage endCursor }',
    '      edges {',
    '        node {',
    '          handle',
    '          vendor',
    '          priceRange { minVariantPrice { amount } }',
    '          productType',
    '          tags',
    '          quoteDev: metafield(namespace: "spec", key: "device_type") { value }',
    '          quoteBody: metafield(namespace: "spec", key: "body_material") { value }',
    '          availableForSale',
    '          onlineStoreUrl',
    '          variants(first: 1) { nodes { currentlyNotInStock } }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  // Consulta completa para renderizar tarjetas (Fase B) — mismos campos que
  // usa hub-storefront-filters.js para que cardHtml produzca HTML idéntico.
  var AVAILABILITY_PRODUCTS_QUERY = [
    'query CollectionAvailabilityProducts($handle: String!, $first: Int, $after: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean, $filters: [ProductFilter!]) {',
    '  collection(handle: $handle) {',
    '    products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {',
    '      pageInfo { hasNextPage hasPreviousPage endCursor }',
    '      edges {',
    '        node {',
    '          title',
    '          handle',
    '          vendor',
    '          productType',
    '          tags',
    '          quoteDev: metafield(namespace: "spec", key: "device_type") { value }',
    '          quoteBody: metafield(namespace: "spec", key: "body_material") { value }',
    '          availableForSale',
    '          onlineStoreUrl',
    '          featuredImage { url altText width height }',
    '          images(first: 2) { nodes { url altText } }',
    '          priceRange { minVariantPrice { amount } }',
    '          compareAtPriceRange { maxVariantPrice { amount } }',
    '          variants(first: 1) { nodes { id availableForSale currentlyNotInStock } }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  var AVAILABILITY_CHUNK_SIZE = 250; // máximo `first` que acepta la Storefront API
  // 60 x 250 = hasta 15,000 productos por conteo. Antes eran 40 (10,000),
  // y la colección más grande de esta tienda (watts, 10,299 productos)
  // quedaba justo por encima: devolvía 164 / 9,836 en vez de 168 / 10,131,
  // sin avisar que el número estaba incompleto. Si aun así se alcanza el
  // techo, computeAvailabilityCounts marca truncated y no se muestra nada.
  var MAX_AVAILABILITY_COUNT_FETCHES = 60;
  var MAX_AVAILABILITY_RAW_FETCHES = 20; // hasta 5,000 productos crudos escaneados por página filtrada

  /* ---------------------------------------------------------------------
   * Tarjeta de producto — copiada de HubStorefrontFilters.prototype.cardHtml,
   * parametrizada con `ctx` (la instancia de CollectionAvailabilityFilter)
   * en vez de `this`, para poder reutilizarla como función suelta.
   * ------------------------------------------------------------------- */
  function priceHtml(product, ctx) {
    // Calentadores State Water Heaters: sin precio; se reserva el renglón (invisible) para igualar alturas.
    if (window.CSQuote && window.CSQuote.hidesPrice(product)) {
      return '<div class="product-item__price-list price-list product-item__price-list--placeholder" aria-hidden="true"><span class="price">&nbsp;</span></div>';
    }
    var min = product.priceRange.minVariantPrice;
    var compareMax = product.compareAtPriceRange && product.compareAtPriceRange.maxVariantPrice;
    var onSale = compareMax && parseFloat(compareMax.amount) > parseFloat(min.amount);
    var html = '<div class="product-item__price-list price-list">';
    if (onSale) {
      html += '<span class="price price--highlight">' + formatMoney(min.amount, ctx.moneyFormat) + '</span>';
      html += '<span class="price price--compare">' + formatMoney(compareMax.amount, ctx.moneyFormat) + '</span>';
    } else {
      html += '<span class="price">' + formatMoney(min.amount, ctx.moneyFormat) + '</span>';
    }
    html += '</div>';
    return html;
  }

  function inventoryBadgeHtml(product, ctx) {
    // A pedido: descontinuado (availableForSale=false: sin stock y no se vende sin inventario) -> "Out of Stock", no Special Order.
    if (!(window.CSQuote && window.CSQuote.isQuote(product)) && !product.availableForSale) {
      return '<span class="product-item__inventory inventory inventory--out">Out of Stock</span>';
    }
    if (productIsNotInStock(product)) {
      return '<button type="button" class="product-item__inventory inventory inventory--special-order" data-action="open-modal" aria-controls="modal-special-order-policy-' + ctx.sectionId + '">Special Order</button>';
    }
    return '<span class="product-item__inventory inventory inventory--high">In Stock</span>';
  }

  function cardHtml(product, ctx) {
    var variant = product.variants.nodes[0];
    var img = product.featuredImage;

    var sizeClass = ctx.productImageSize !== 'natural' ? ' aspect-ratio--' + ctx.productImageSize : '';
    var aspectRatioStyle = '';
    var imgHtml = '';
    if (img) {
      var ratio = img.width && img.height ? (img.width / img.height) : 1;
      aspectRatioStyle = ' style="padding-bottom: ' + (100 / ratio) + '%"';
      imgHtml = '<img src="' + escapeHtml(img.url) + '" alt="' + escapeHtml(img.altText || product.title) + '" loading="lazy" width="' + img.width + '" height="' + img.height + '" class="product-item__primary-image">';
    }

    var wrapperClass = 'product-item__image-wrapper';
    var secondaryImages = (product.images && product.images.nodes) || [];
    if (ctx.showSecondaryImage && secondaryImages.length > 1) {
      wrapperClass += ' product-item__image-wrapper--with-secondary';
      var secondImg = secondaryImages[1];
      imgHtml += '<img src="' + escapeHtml(secondImg.url) + '" alt="' + escapeHtml(secondImg.altText || product.title) + '" loading="lazy" class="product-item__secondary-image">';
    }

    var addToCart = '';
    if (window.CSQuote && window.CSQuote.isQuote(product)) {
      addToCart = ''
        + '<div class="product-item__action-list button-stack">'
        + '  <a href="/products/' + escapeHtml(product.handle) + '" class="product-item__action-button button button--small button--primary">Get A Quote</a>'
        + '</div>';
    } else if (variant) {
      var numericVariantId = variant.id.replace('gid://shopify/ProductVariant/', '');
      var disabled = product.availableForSale ? '' : ' disabled';
      var btnClass = 'product-item__action-button button button--small button--primary' + (product.availableForSale ? '' : ' button--disabled');
      addToCart = ''
        + '<form action="/cart/add" method="post" class="product-item__action-list button-stack">'
        + '  <input type="hidden" name="quantity" value="1">'
        + '  <input type="hidden" name="id" value="' + numericVariantId + '">'
        + '  <buy-button class="buy-button">'
        + '    <button type="submit" class="' + btnClass + '"' + disabled + '>' + (product.availableForSale ? 'Add To Cart' : 'Sold out') + '</button>'
        + '  </buy-button>'
        + '</form>';
    }

    var vendorHtml = ctx.showVendor
      ? '<a class="product-item__vendor link" href="/collections/' + handleize(product.vendor) + '">' + escapeHtml(product.vendor) + '</a>'
      : '';

    return ''
      + '<div class="product-item product-item--vertical' + (ctx.gridClasses ? ' ' + ctx.gridClasses : '') + '">'
      + '  <a href="/products/' + escapeHtml(product.handle) + '" class="' + wrapperClass + '">'
      + '    <div class="aspect-ratio' + sizeClass + '"' + aspectRatioStyle + '>' + imgHtml + '</div>'
      + '  </a>'
      + '  <div class="product-item__info">'
      + '    <div class="product-item__info-inner">'
      + vendorHtml
      + '      <a href="/products/' + escapeHtml(product.handle) + '" class="product-item__title text--strong link">' + escapeHtml(product.title) + '</a>'
      + priceHtml(product, ctx)
      + inventoryBadgeHtml(product, ctx)
      + '    </div>'
      + addToCart
      + '  </div>'
      + '</div>';
  }

  /* ---------------------------------------------------------------------
   * Paginación Previous/Next — mismo estilo visual que snippets/hub-pagination.liquid
   * (Nivel 1 ya la carga; Nivel 3 no, así que se inyecta una sola vez por
   * JS, igual que hub-storefront-filters.js hace con su slider de precio).
   * No se reutiliza el jump-to-page nativo: un salto de página arbitrario
   * no tiene sentido con acumulación (una página cruda del servidor no
   * equivale a una página lógica ya filtrada).
   * ------------------------------------------------------------------- */
  var paginationStylesInjected = false;
  function injectPaginationStyles() {
    if (paginationStylesInjected) return;
    paginationStylesInjected = true;
    var style = document.createElement('style');
    style.textContent = [
      '.hub-pagination { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 0.5rem; margin-top: 2rem; }',
      '.hub-pagination__btn { background: #fff; border: 1px solid #ccc; border-radius: 4px; padding: 0.5rem 1rem; font-size: 0.85rem; font-weight: 600; color: #1a1a1a; cursor: pointer; transition: background 0.15s ease, border-color 0.15s ease; }',
      '.hub-pagination__btn:hover:not(:disabled) { background: #f5f5f5; }',
      '.hub-pagination__btn:disabled { color: #bbb; border-color: #e5e5e5; cursor: not-allowed; }',
      '.hub-pagination__btn--accent { border-color: #004a9d; color: #004a9d; }',
      '.hub-pagination__btn--accent:hover:not(:disabled) { background: #004a9d; color: #fff; }',
      '.hub-pagination__btn--accent:disabled { border-color: #e5e5e5; color: #bbb; }',
      '.hub-pagination__of { color: #666; white-space: nowrap; }',
    ].join('\n');
    document.head.appendChild(style);
  }

  function readActiveFiltersJson(root) {
    var scriptEl = root.querySelector('[data-active-filters-json]');
    if (!scriptEl) return [];
    try {
      var parsed = JSON.parse(scriptEl.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  // sections/main-collection.liquid le pasa grid_classes (ej. "1/3--tablet
  // 1/4--lap-and-up") a snippets/product-item.liquid, concatenado directo
  // en el class del <div class="product-item ..."> — esas clases (no un
  // CSS grid) son las que fijan el ancho responsive real de la tarjeta;
  // sin ellas, la tarjeta no tiene ancho asignado y termina viéndose a su
  // tamaño natural (mucho más grande que el resto de la grilla) apenas
  // Availability toma control de la grilla — bug real, reproducido: con
  // pocos resultados (ej. 2 productos) la diferencia de tamaño era obvia.
  // section.settings.products_per_row no llega a este JS ni falta que
  // llegue: gridClasses ya viene calculado del lado de Liquid (ver
  // "gridClasses" en section_settings, data-section-settings del <section>
  // envolvente) — alcanza con leerlo una sola vez acá. sections/collection-
  // hub.liquid (Nivel 1, que también usa este módulo) no pasa grid_classes
  // en absoluto — su grilla no depende de esto, así que ahí simplemente no
  // se encuentra nada y gridClasses queda '', sin efecto.
  function readGridClasses(sectionId) {
    if (!sectionId) return '';
    var sectionEl = document.querySelector('[data-section-id="' + sectionId + '"]');
    if (!sectionEl || !sectionEl.dataset.sectionSettings) return '';
    try {
      var parsed = JSON.parse(sectionEl.dataset.sectionSettings);
      return parsed.gridClasses || '';
    } catch (e) {
      return '';
    }
  }

  /* ---------------------------------------------------------------------
   * Controlador
   * ------------------------------------------------------------------- */

  // Deja las casillas de Availability inertes y explicadas cuando este
  // módulo no puede funcionar (falta el token de Storefront, por ejemplo).
  // Se desactiva la casilla en sí para que el handler delegado de theme.js
  // ([name^="filter."]) tampoco dispare el filtrado nativo impreciso.
  function disableAvailabilityCheckboxes() {
    var boxes = document.querySelectorAll('[data-availability-checkbox]');
    boxes.forEach(function (box) {
      box.disabled = true;
      // Marca de "desactivada A PROPÓSITO". El sidebar reactiva las
      // casillas después de un envío del form (ver collection-filters.liquid),
      // y sin esta marca esa reactivación deshacía el modo degradado y
      // devolvía el control al filtro nativo impreciso.
      box.setAttribute('data-availability-unavailable', '');
      var label = box.closest('label') || box.parentNode;
      if (label && label.setAttribute) {
        label.setAttribute('title', 'Availability filtering is temporarily unavailable.');
      }
    });
    document.querySelectorAll('[data-availability-count-for]').forEach(function (span) {
      span.textContent = '';
    });
  }

  // Mapea el `sort_by` de Shopify al par sortKey/reverse de la Storefront
  // API — mismo case que snippets/collection-filters.liquid resuelve en
  // Liquid (sf_sort_key / sf_reverse); si se tocan allá, tocar acá.
  var SORT_BY_MAP = {
    'best-selling': ['BEST_SELLING', false],
    'title-ascending': ['TITLE', false],
    'title-descending': ['TITLE', true],
    'price-ascending': ['PRICE', false],
    'price-descending': ['PRICE', true],
    'created-descending': ['CREATED', true],
    'created-ascending': ['CREATED', false],
    'manual': ['MANUAL', false],
  };

  // BUG real: el orden se leía UNA sola vez, del atributo data-sort-key del
  // sidebar, en el constructor. Pero cambiar "Sort by" no recarga la
  // página: theme.js hace _reload() por AJAX (_sortByChanged) y solo
  // reemplaza .collection__dynamic-part — y el sidebar (.cf-filters, donde
  // vive data-sort-key) queda FUERA de esa parte, así que nunca se
  // actualiza. Resultado: ordenar por precio y después marcar "In Stock"
  // devolvía los productos en el orden viejo, distinto del que el visitante
  // acababa de ver. La fuente de verdad del orden vigente es la URL, que
  // es donde _sortByChanged lo deja con pushState.
  function readSortFromUrl(fallbackKey, fallbackReverse) {
    var sortBy = null;
    try {
      sortBy = new URLSearchParams(window.location.search).get('sort_by');
    } catch (e) { sortBy = null; }
    var mapped = sortBy ? SORT_BY_MAP[sortBy] : null;
    if (!mapped) return { sortKey: fallbackKey, reverse: fallbackReverse };
    return { sortKey: mapped[0], reverse: mapped[1] };
  }

  function CollectionAvailabilityFilter(root) {
    this.root = root;
    this.handle = root.dataset.collectionHandle;

    var token = root.dataset.storefrontToken;
    var shopDomain = root.dataset.shopDomain;
    var apiVersion = root.dataset.storefrontApiVersion;
    if (!this.handle || !token || !shopDomain || !apiVersion) {
      // Antes esto se iba en silencio y las dos casillas quedaban
      // conectadas al filtro NATIVO de Shopify, que es justamente el
      // impreciso: agrupa "stock real" con "continue-selling en 0", así
      // que bajo "In Stock" aparecían tarjetas rotuladas Special Order
      // — exactamente la contradicción que este módulo existe para evitar.
      // Sin credenciales no hay forma de dar un resultado correcto, así
      // que se desactivan en vez de mostrar uno equivocado.
      disableAvailabilityCheckboxes();
      return;
    }

    this.gridSelector = root.dataset.gridSelector;
    this.paginationSelector = root.dataset.paginationSelector;
    this.captureDomRefs();

    this.client = new StorefrontClient(shopDomain, token, apiVersion);
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.pageSize = parseInt(root.dataset.pageSize, 10) || 24;
    this.sectionId = root.dataset.sectionId || '';
    this.gridClasses = readGridClasses(this.sectionId);
    this.productImageSize = root.dataset.productImageSize || 'natural';
    this.showSecondaryImage = root.dataset.showSecondaryImage === 'true';
    this.showVendor = root.dataset.showVendor !== 'false';
    this.ssrSortKey = root.dataset.sortKey || 'COLLECTION_DEFAULT';
    this.ssrSortReverse = root.dataset.sortReverse === 'true';
    this.refreshSort();

    this.availabilityFilter = null; // true=In Stock, false=Special Order — nunca se manda a la API
    this.resetPagination();

    this.wireAvailabilityCheckboxes();
    this.patchAvailabilityCounts();
    this.restoreStateFromUrl();
  }

  // Extraído de lo que antes vivía inline en el constructor, para poder
  // volver a correrlo después de un reload AJAX (ver handleThemeReload más
  // abajo) — sin esto, this.grid/this.paginationMount quedan apuntando a
  // nodos ya desmontados en cuanto CUALQUIER otro filtro (Brand, Price...)
  // dispara el _reload() nativo del theme (theme.js, CollectionSection),
  // que reemplaza el innerHTML completo de .collection__dynamic-part —
  // bug real, reproducido: el conteo de Availability se actualizaba
  // (vive fuera de .collection__dynamic-part) pero la grilla visible no
  // (vive adentro), porque se seguía escribiendo sobre el nodo viejo.
  CollectionAvailabilityFilter.prototype.captureDomRefs = function () {
    var previousGrid = this.grid;
    this.grid = this.gridSelector ? document.querySelector(this.gridSelector) : null;
    this.paginationMount = this.paginationSelector ? document.querySelector(this.paginationSelector) : null;
    // "theme:loading:end" NO significa necesariamente que la grilla se haya
    // vuelto a renderizar: el carrito también lo dispara (añadir al
    // carrito, estimador de envío, refresh del mini-cart — ver theme.js).
    // La señal confiable de "esto vino del servidor de nuevo" es que el
    // NODO de la grilla haya cambiado de identidad: _reload() reemplaza el
    // contenido de .collection__dynamic-part, así que querySelector
    // devuelve un nodo nuevo. Si es el mismo nodo, lo que hay adentro es
    // lo que dejó goLive() (ya filtrado) y volver a fotografiarlo dejaba
    // la grilla filtrada como si fuera el original — al desmarcar
    // Availability se "restauraba" esa grilla filtrada, de forma
    // permanente. Reproducido: añadir al carrito y luego desmarcar.
    this.gridWasRerendered = this.grid !== previousGrid;
    // Sin grid o sin mount de paginación no hay dónde renderizar la Fase B,
    // pero la Fase A (conteo) no depende de ninguno de los dos — así que
    // solo se corta acá si falta lo que hace falta para AMBAS fases; el
    // conteo se calcula igual más abajo aunque falte alguno de los mounts.

    // A pedido (auditoría móvil): guarda el SSR original tal cual llegó,
    // antes de que goLive() lo pise — así, al desmarcar Availability, se
    // puede restaurar sin un reload completo de página (que antes cerraba
    // de golpe el drawer de filtros en móvil si estaba abierto).
    if (this.gridWasRerendered) {
      this.originalGridHtml = this.grid ? this.grid.innerHTML : null;
      this.originalPaginationHtml = this.paginationMount ? this.paginationMount.innerHTML : null;
    }
    // Bug real: captureDomRefs() corre en CADA "theme:loading:end", y el
    // sidebar no se vuelve a renderizar en esas recargas AJAX — así que si
    // Availability ya estaba activo, lo que hay en el DOM es el número que
    // YA parcheó patchHeadingCount (el filtrado), no el original. Volver a
    // fotografiarlo ahí hacía que, al desmarcar, se "restaurara" el conteo
    // filtrado como si fuera el total. Solo se fotografía cuando no hay
    // Availability activo, que es cuando el DOM sí tiene el valor nativo.
    if (this.availabilityFilter == null) {
      var originalHeadingCountEl = this.root.querySelector('.cf-heading__count');
      var originalCaptionCountEl = document.querySelector('.hub-pagination-caption__count');
      this.originalHeadingCountText = originalHeadingCountEl ? originalHeadingCountEl.textContent : null;
      this.originalCaptionCountText = originalCaptionCountEl ? originalCaptionCountEl.textContent : null;
    }

    // Igual de importante: los OTROS filtros activos (Brand, Price...) que
    // esta instancia le manda a la Storefront API junto con Availability.
    // [data-active-filters-json] no vive dentro de .collection__dynamic-part
    // (ver .cf-filters en collection-filters.liquid), así que _reload() no
    // lo tocaba — quedaba con el combo de filtros de cuando cargó la
    // página, ignorando cualquier filtro nuevo elegido después. theme.js
    // ahora lo refresca en cada _reload() (ver _reload ahí mismo); acá solo
    // se vuelve a leer.
    this.otherFilters = readActiveFiltersJson(this.root);
    // El orden también puede haber cambiado en esta recarga AJAX (Sort by
    // no recarga la página, ver readSortFromUrl).
    this.refreshSort();
  };

  CollectionAvailabilityFilter.prototype.resetPagination = function () {
    this.afterHistory = [null];
    this.availabilityBufferHistory = [[]];
    this.pageIndex = 0;
    this.lastPageInfo = null;
    this.rawExhaustedFromPage = null;
  };

  // Se llama en cada "theme:loading:end" (disparado al final de CADA
  // _reload() nativo, sea por otro filtro, orden o paginación nativa — ver
  // theme.js). Vuelve a capturar todo lo que _reload() acaba de reemplazar
  // y, si Availability seguía marcado en ese momento, reaplica el filtro
  // sobre el nuevo combo de filtros en vez de dejar la grilla mostrando el
  // resultado nativo (sin filtrar) que el reload acaba de imprimir.
  //
  // patchAvailabilityCounts() también se vuelve a correr SIEMPRE (no solo
  // si Availability está activo): recalcula "(N)" junto a las dos casillas
  // contra el combo de filtros que acaba de quedar (this.otherFilters, ya
  // refrescado por captureDomRefs() arriba) — bug real reproducido: al
  // combinar Availability con OTRO filtro nativo (ej. Country of Origin),
  // tanto la grilla como el conteo entre paréntesis se quedaban pegados en
  // el total de ANTES de agregar ese otro filtro.
  CollectionAvailabilityFilter.prototype.handleThemeReload = function () {
    // Modo degradado (sin credenciales): el constructor salió temprano, así
    // que no hay cliente de Storefront. Antes esto seguía igual y terminaba
    // llamando a this.client.query sobre undefined, lanzando un TypeError
    // sincrónico que salía del listener sin ningún .catch que lo tomara.
    if (!this.client) return;
    if (!document.body.contains(this.root)) return;
    this.captureDomRefs();
    // Si la grilla no se volvió a renderizar, este "theme:loading:end" no
    // vino de un cambio de filtro/orden/página sino del carrito (añadir al
    // carrito, estimador de envío, etc.). Antes igual se reseteaba la
    // paginación y se re-filtraba: un visitante parado en la página 7
    // volvía de golpe a la 1 por agregar un producto al carrito
    // (reproducido). No hay nada que rehacer en ese caso.
    if (!this.gridWasRerendered) return;
    this.resetPagination();
    this.patchAvailabilityCounts();
    if (this.availabilityFilter !== null) {
      this.goLive();
    }
  };

  CollectionAvailabilityFilter.prototype.buildVariables = function () {
    return {
      handle: this.handle,
      first: this.pageSize,
      after: this.afterHistory[this.pageIndex] || null,
      sortKey: this.sortKey,
      reverse: this.reverse,
      filters: this.otherFilters,
    };
  };

  /* ---- Fase A: conteo real ---- */
  CollectionAvailabilityFilter.prototype.computeAvailabilityCounts = function () {
    var self = this;
    var vars = this.buildVariables();
    // Firma del estado que determina el conteo (los OTROS filtros activos
    // + la colección). Sirve para dos cosas que antes faltaban acá y que
    // el motor de Nivel 1/2 sí tenía:
    //   1. Caché: "theme:loading:end" se dispara en CADA recarga nativa
    //      (otro filtro, orden, paginación), y cada una relanzaba el
    //      escaneo completo — hasta 60 peticiones repetidas por nada.
    //   2. Anti-carrera: dos escaneos en vuelo pueden resolverse fuera de
    //      orden y el viejo escribía el conteo del filtro ANTERIOR encima
    //      del nuevo. Ahora el resultado viaja con su firma y quien lo
    //      consume descarta el que ya no corresponde.
    var signature = JSON.stringify(this.otherFilters) + '|' + this.handle;
    if (this.availabilityCountsSignature === signature && this.availabilityCounts) {
      return Promise.resolve(this.availabilityCounts);
    }
    // Deduplicación en vuelo: si ya hay un escaneo corriendo para ESTA
    // misma firma, se devuelve esa promesa en vez de lanzar un segundo
    // recorrido completo (en watts eran 42 peticiones duplicadas al pedo
    // cuando dos renders se pisaban antes de que el primero resolviera).
    if (this.inFlightCountsSignature === signature && this.inFlightCounts) {
      return this.inFlightCounts;
    }

    vars.first = AVAILABILITY_CHUNK_SIZE;
    var inStock = 0;
    var specialOrder = 0;
    var fetches = 0;
    var truncated = false;

    function step(after) {
      vars.after = after;
      return self.client.query(AVAILABILITY_COUNT_QUERY, vars).then(function (data) {
        var conn = data && data.collection && data.collection.products;
        if (!conn) return;
        fetches++;
        (conn.edges || []).forEach(function (edge) {
          if (productIsNotInStock(edge.node)) {
            specialOrder++;
          } else {
            inStock++;
          }
        });
        if (conn.pageInfo.hasNextPage) {
          if (fetches < MAX_AVAILABILITY_COUNT_FETCHES) {
            return step(conn.pageInfo.endCursor);
          }
          // Se alcanzó el techo de escaneo y la colección sigue: el conteo
          // que tenemos es PARCIAL. Antes se devolvía igual como si fuera
          // exacto — medido en vivo: Watts (10,299 productos) reportaba
          // 164 / 9,836 en vez de 168 / 10,131, con 295 productos
          // descartados en silencio. Ahora se marca para no mostrar un
          // número que parece exacto y no lo es.
          truncated = true;
        }
      });
    }

    var inFlight = step(null).then(function () {
      var result = {
        inStock: inStock,
        specialOrder: specialOrder,
        truncated: truncated,
        signature: signature,
      };
      // Solo se guarda en caché si esta firma SIGUE siendo la vigente. Un
      // escaneo viejo que resuelve tarde (lo habitual: el combo nuevo es
      // más chico y termina antes) pisaba la caché válida con la suya;
      // currentCounts() después la rechazaba por firma y el "restaurar al
      // desmarcar" caía al valor de carga de página, que ya no servía.
      // Reproducido: se esperaba (80) y salía (1000).
      if (self.signatureNow() === signature) {
        self.availabilityCounts = result;
        self.availabilityCountsSignature = signature;
      }
      return result;
    });

    this.inFlightCountsSignature = signature;
    this.inFlightCounts = inFlight;
    var clearInFlight = function () {
      if (self.inFlightCountsSignature === signature) {
        self.inFlightCounts = null;
        self.inFlightCountsSignature = null;
      }
    };
    inFlight.then(clearInFlight, clearInFlight);

    return inFlight;
  };

  CollectionAvailabilityFilter.prototype.patchAvailabilityCounts = function () {
    var self = this;
    // Segunda red de contención del modo degradado (ver handleThemeReload):
    // sin cliente no hay nada que contar.
    if (!this.client) return;
    // document-wide a propósito: el drawer de filtros para móvil
    // (#mobile-collection-filters-form, en main-collection.liquid) es un
    // <form> totalmente separado del sidebar de escritorio (.cf-filters,
    // this.root) — trae su PROPIA copia de las dos casillas/conteos de
    // Availability. Se patchean AMBAS copias con el mismo resultado.
    var trueSpans = document.querySelectorAll('[data-availability-count-for="true"]');
    var falseSpans = document.querySelectorAll('[data-availability-count-for="false"]');
    if (!trueSpans.length && !falseSpans.length) return;
    this.computeAvailabilityCounts().then(function (counts) {
      // Anti-carrera: si mientras este escaneo estaba en vuelo el visitante
      // cambió otro filtro, el resultado que acaba de llegar es del estado
      // ANTERIOR — se descarta en vez de pisar el conteo correcto.
      var currentSignature = JSON.stringify(self.otherFilters) + '|' + self.handle;
      if (counts.signature !== currentSignature) return;
      self.availabilityCounts = counts;
      // Con conteo parcial (colección más grande que el techo de escaneo)
      // se deja el espacio vacío en vez de mostrar un número incompleto
      // que el visitante leería como exacto.
      if (counts.truncated) return;
      trueSpans.forEach(function (span) { span.textContent = '(' + counts.inStock + ')'; });
      falseSpans.forEach(function (span) { span.textContent = '(' + counts.specialOrder + ')'; });
      // Si el visitante ya había marcado Availability antes de que este
      // cálculo (lento, hasta 10,000 productos) terminara, el heading
      // "Filter & Sort (N)" también se corrige acá, tarde pero corregido.
      if (self.availabilityFilter !== null) self.patchHeadingCount();
    }).catch(function (err) {
      console.error('[collection-availability-filter] no se pudo calcular el conteo real de Availability:', err);
    });
  };

  // "Filter & Sort (N)" y "Título (N items)" son conteos NATIVOS de Shopify
  // (paginate.items, ya renderizados en el SSR) — igual que el conteo junto
  // a cada casilla, no distinguen stock real de "sigue vendiendo con
  // cantidad 0", así que con Availability activo siguen mostrando el total
  // SIN filtrar. Se corrigen acá con el mismo escaneo real ya calculado en
  // patchAvailabilityCounts (una sola vez, cacheado en this.availabilityCounts).
  // Devuelve el conteo cacheado SOLO si corresponde al combo de filtros
  // vigente. this.availabilityCounts puede quedar con el resultado de un
  // escaneo anterior (se escribe apenas resuelve, incluso si quien lo pidió
  // después lo descarta por firma vieja), así que todo el que lo lea tiene
  // que revalidar — si no, "Filter & Sort (N)" y el "of N pages" quedaban
  // mostrando el número del filtro ANTERIOR, de forma permanente si el
  // escaneo nuevo terminaba descartándose.
  // El orden vigente sale de la URL (ver readSortFromUrl); el atributo del
  // SSR solo sirve de respaldo para la primera carga, cuando todavía no
  // hay ningún sort_by en la URL.
  CollectionAvailabilityFilter.prototype.refreshSort = function () {
    var sort = readSortFromUrl(this.ssrSortKey, this.ssrSortReverse);
    this.sortKey = sort.sortKey;
    this.reverse = sort.reverse;
  };

  CollectionAvailabilityFilter.prototype.signatureNow = function () {
    return JSON.stringify(this.otherFilters) + '|' + this.handle;
  };

  CollectionAvailabilityFilter.prototype.currentCounts = function () {
    var counts = this.availabilityCounts;
    if (!counts) return null;
    return counts.signature === this.signatureNow() ? counts : null;
  };

  CollectionAvailabilityFilter.prototype.patchHeadingCount = function () {
    var counts = this.currentCounts();
    if (!counts || this.availabilityFilter === null) return;
    // Conteo parcial: no se corrige el heading con un número incompleto —
    // se deja el nativo, que al menos no pretende ser el total filtrado.
    if (counts.truncated) return;
    var accurateTotal = this.availabilityFilter ? counts.inStock : counts.specialOrder;
    var headingCount = this.root.querySelector('.cf-heading__count');
    if (headingCount) headingCount.textContent = '(' + accurateTotal.toLocaleString('en-US') + ')';
    // .hub-pagination-caption__count vive fuera de .cf-filters (junto a la
    // grilla, no en el sidebar) — solo hay una por página, document-wide
    // está bien.
    var captionCount = document.querySelector('.hub-pagination-caption__count');
    if (captionCount) captionCount.textContent = '(' + accurateTotal.toLocaleString('en-US') + ' items)';
    // El "of N pages" de la barra Previous/Next (ver renderPagination) lee
    // this.availabilityCounts — si ya estamos en vivo (el visitante ya
    // marcó Availability y goLive() ya corrió al menos una vez), se vuelve
    // a pintar para que se corrija en cuanto el escaneo real termina.
    if (this.lastPageInfo) this.renderPagination();
  };

  /* ---- Fase B: filtrado real ---- */
  CollectionAvailabilityFilter.prototype.filterEdgesByAvailability = function (edges) {
    var wantInStock = this.availabilityFilter === true;
    return edges.filter(function (edge) {
      var notInStock = productIsNotInStock(edge.node);
      return wantInStock ? !notInStock : notInStock;
    });
  };

  // Sort by (y otros controles que envían el <form> de forma nativa, con
  // recarga completa) pierde la elección de Availability: ese estado solo
  // vivía en memoria, y las dos casillas se deshabilitan a propósito antes
  // de cada envío para que el parámetro NATIVO (impreciso) no viaje. Se
  // guarda entonces en un parámetro propio de la URL (?avail=in|special) y
  // en un <input type="hidden"> dentro de cada <form> con casillas de
  // Availability, así cualquier envío nativo lo conserva; al cargar, si la
  // URL lo trae, se vuelve a marcar la casilla y se reaplica el filtro.
  CollectionAvailabilityFilter.prototype.persistState = function () {
    var value = this.availabilityFilter === null ? null : (this.availabilityFilter ? 'in' : 'special');
    document.querySelectorAll('form').forEach(function (form) {
      if (!form.querySelector('[data-availability-checkbox]')) return;
      var hidden = form.querySelector('input[type="hidden"][data-availability-persist]');
      if (value === null) {
        if (hidden) hidden.remove();
        return;
      }
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'avail';
        hidden.setAttribute('data-availability-persist', '');
        form.appendChild(hidden);
      }
      hidden.value = value;
    });
    try {
      var url = new URL(window.location.href);
      if (value === null) url.searchParams.delete('avail');
      else url.searchParams.set('avail', value);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) { /* URL no editable (iframe de preview): no pasa nada */ }
  };

  CollectionAvailabilityFilter.prototype.restoreStateFromUrl = function () {
    var param = null;
    try { param = new URLSearchParams(window.location.search).get('avail'); } catch (e) { return; }
    if (param !== 'in' && param !== 'special') return;
    var wanted = param === 'in' ? 'true' : 'false';
    var box = null;
    document.querySelectorAll('[data-availability-checkbox]').forEach(function (candidate) {
      if (!box && candidate.dataset.availabilityCheckbox === wanted && !candidate.disabled &&
          !candidate.hasAttribute('data-availability-unavailable')) box = candidate;
    });
    if (!box) return;
    box.checked = true;
    box.dispatchEvent(new Event('change'));
  };

  CollectionAvailabilityFilter.prototype.wireAvailabilityCheckboxes = function () {
    var self = this;
    // document-wide (no this.root): el drawer de filtros para móvil trae su
    // PROPIA copia de estas dos casillas, en un <form> separado del
    // sidebar de escritorio — se tratan como un solo grupo de 2-4 elementos
    // para que ambas copias queden sincronizadas sin importar cuál tocó el
    // visitante.
    var boxes = document.querySelectorAll('[data-availability-checkbox]');
    if (!boxes.length || !this.grid || !this.paginationMount) return;

    boxes.forEach(function (box) {
      box.addEventListener('change', function (event) {
        // CRÍTICO: el theme trae su PROPIO filtrado nativo por AJAX
        // (theme.js, CollectionTemplate._onFilterChanged), enganchado a
        // "change" en CUALQUIER campo cuyo name empiece con "filter." —
        // exactamente el mismo patrón que usan estas dos casillas
        // (name="filter.v.availability"), vía delegación de eventos sobre
        // toda la sección. Ese listener nativo es TOTALMENTE independiente
        // de este script: no pasa por submitFilters() ni por el "submit"
        // del <form>, arma su propio fetch() y hace history.pushState()
        // directamente con el resultado nativo (inexacto) de Availability.
        // event.stopPropagation() corta el evento ANTES de que llegue a
        // burbujear hasta ese listener delegado — sin esto, cada clic acá
        // disparaba a la vez esta lógica (correcta) Y esa lógica nativa
        // (inexacta), y la nativa terminaba ganando la carrera visualmente.
        event.stopPropagation();

        if (!box.checked) {
          // A pedido (auditoría móvil): antes hacía window.location.reload()
          // acá — si el drawer de filtros móvil estaba abierto, se cerraba
          // de golpe con el reload, inconsistente con el resto de las
          // interacciones (que sí actualizan en vivo sin recargar). Ahora
          // restaura el HTML original de grilla/paginación tal cual llegó
          // por SSR, sin tocar la URL (nunca se le agregó ningún parámetro
          // de Availability, así que no hay nada que "deshacer" ahí).
          self.availabilityFilter = null;
          // Desmarcar tiene que apagar AMBAS copias de la casilla (sidebar
          // de escritorio y drawer móvil son dos <form> distintos con su
          // propia copia). Esta rama salía antes del bucle de sincronización
          // de más abajo, así que la otra copia se quedaba tildada aunque
          // el filtro ya estuviera apagado.
          boxes.forEach(function (other) { other.checked = false; });
          if (self.grid && self.originalGridHtml !== null) {
            self.grid.innerHTML = self.originalGridHtml;
          }
          if (self.paginationMount && self.originalPaginationHtml !== null) {
            self.paginationMount.innerHTML = self.originalPaginationHtml;
          }
          // El total "sin Availability" del combo de filtros VIGENTE es,
          // exactamente, la suma de los dos baldes del último escaneo real
          // — y ese escaneo ya respeta los otros filtros activos. Se
          // prefiere sobre self.originalHeadingCountText, que es una foto
          // tomada al cargar la página: si el visitante cambió otro filtro
          // por AJAX mientras tenía Availability puesto, esa foto quedó
          // vieja (el sidebar no se re-renderiza en esas recargas) y al
          // desmarcar se "restauraba" un total que ya no correspondía.
          var restoreCounts = self.currentCounts();
          var restoredTotal = null;
          if (restoreCounts && !restoreCounts.truncated) {
            restoredTotal = restoreCounts.inStock + restoreCounts.specialOrder;
          }
          var headingCountEl = self.root.querySelector('.cf-heading__count');
          if (headingCountEl) {
            if (restoredTotal !== null) {
              headingCountEl.textContent = '(' + restoredTotal.toLocaleString('en-US') + ')';
            } else if (self.originalHeadingCountText !== null) {
              headingCountEl.textContent = self.originalHeadingCountText;
            }
          }
          var captionCountEl = document.querySelector('.hub-pagination-caption__count');
          if (captionCountEl) {
            if (restoredTotal !== null) {
              captionCountEl.textContent = '(' + restoredTotal.toLocaleString('en-US') + ' items)';
            } else if (self.originalCaptionCountText !== null) {
              captionCountEl.textContent = self.originalCaptionCountText;
            }
          }
          self.persistState();
          self.pageIndex = 0;
          self.afterHistory = [null];
          self.availabilityBufferHistory = [[]];
          self.lastPageInfo = null;
          self.rawExhaustedFromPage = null;
          return;
        }
        boxes.forEach(function (other) {
          other.checked = other.dataset.availabilityCheckbox === box.dataset.availabilityCheckbox;
        });
        self.availabilityFilter = box.dataset.availabilityCheckbox === 'true';
        self.persistState();
        self.pageIndex = 0;
        self.afterHistory = [null];
        self.availabilityBufferHistory = [[]];
        self.rawExhaustedFromPage = null;
        self.patchHeadingCount();
        self.goLive();
      });
    });
  };

  // Mismo patrón de acumulación que HubStorefrontFilters.prototype.fetchAndRender:
  // escanea páginas crudas de AVAILABILITY_CHUNK_SIZE, filtra client-side,
  // acumula hasta llenar pageSize, y guarda el sobrante en un buffer indexado
  // por página lógica para no descartar coincidencias de más.
  CollectionAvailabilityFilter.prototype.goLive = function () {
    var self = this;
    this.setLoadingState(true);

    var startAfter = this.afterHistory[this.pageIndex] || null;
    var accumulated = (this.availabilityBufferHistory[this.pageIndex] || []).slice();
    var rawFetchesUsed = 0;

    // Bug real, reproducido en vivo: cuando el recorrido crudo llega al
    // final de la colección, la API devuelve endCursor: null. Antes ese
    // null se guardaba igual como cursor de la página siguiente, y como
    // `afterHistory[i] || null` no distingue "sin cursor" de "arrancar de
    // cero", esa página volvía a escanear DESDE EL PRINCIPIO: productos
    // repetidos y un botón "Next" que no se apagaba nunca (en
    // brass-fittings/In Stock las repeticiones empezaban en la página 5).
    // Si ya no queda nada crudo por recorrer, las páginas siguientes se
    // sirven SOLO con lo que sobró en el buffer, sin volver a consultar.
    var rawExhausted = this.rawExhaustedFromPage != null
      && this.pageIndex >= this.rawExhaustedFromPage;

    function stepOnce(after) {
      var vars = self.buildVariables();
      vars.after = after;
      vars.first = AVAILABILITY_CHUNK_SIZE;
      return self.client.query(AVAILABILITY_PRODUCTS_QUERY, vars).then(function (data) {
        var conn = data && data.collection && data.collection.products;
        if (!conn) return null;
        rawFetchesUsed++;
        var matched = self.filterEdgesByAvailability(conn.edges || []);
        accumulated = accumulated.concat(matched);

        var stillNeedMore = accumulated.length < self.pageSize
          && conn.pageInfo.hasNextPage
          && rawFetchesUsed < MAX_AVAILABILITY_RAW_FETCHES;

        if (stillNeedMore) {
          return stepOnce(conn.pageInfo.endCursor);
        }
        return conn;
      });
    }

    var walk = rawExhausted
      ? Promise.resolve({ pageInfo: { hasNextPage: false, endCursor: null } })
      : stepOnce(startAfter);

    return walk.then(function (conn) {
      self.setLoadingState(false);
      if (!conn) {
        // La peticion fallo: no se sabe si hay productos o no, asi que no
        // se afirma que no los haya.
        self.renderGrid([], false, true);
        self.lastPageInfo = { hasNextPage: false };
        self.renderPagination();
        return;
      }

      var visibleEdges = accumulated.slice(0, self.pageSize);
      self.availabilityBufferHistory[self.pageIndex + 1] = accumulated.slice(self.pageSize);
      var hasLeftoverForNext = (self.availabilityBufferHistory[self.pageIndex + 1] || []).length > 0;

      self.lastPageInfo = { hasNextPage: conn.pageInfo.hasNextPage || hasLeftoverForNext };

      if (conn.pageInfo.hasNextPage) {
        // Solo se guarda un cursor REAL. Nunca el null del final.
        if (self.afterHistory[self.pageIndex + 1] == null) {
          self.afterHistory[self.pageIndex + 1] = conn.pageInfo.endCursor;
        }
      } else if (self.rawExhaustedFromPage == null) {
        // Se acabó la colección: de acá en adelante solo queda el buffer.
        self.rawExhaustedFromPage = self.pageIndex + 1;
      }

      self.renderGrid(visibleEdges, conn.pageInfo.hasNextPage || hasLeftoverForNext);
      self.renderPagination();
    }).catch(function (err) {
      self.setLoadingState(false);
      console.error('[collection-availability-filter] error al filtrar:', err);
    });
  };

  CollectionAvailabilityFilter.prototype.renderGrid = function (edges, moreToScan, failed) {
    if (!this.grid) return;
    if (!edges.length) {
      // Mismo criterio que el motor de Nivel 1/2: si el escaneo se cortó
      // por el tope de peticiones pero la coleccion sigue, decir "no hay
      // productos" es falso — los hay mas adelante.
      if (failed) {
        this.grid.innerHTML = '<p class="hub__no-results">Couldn’t load products just now. Please try again.</p>';
      } else {
        this.grid.innerHTML = moreToScan
          ? '<p class="hub__no-results">Still searching this collection — use Next to keep looking.</p>'
          : '<p class="hub__no-results">No products match the selected filters.</p>';
      }
      return;
    }
    var self = this;
    this.grid.innerHTML = edges.map(function (edge) { return cardHtml(edge.node, self); }).join('');
  };

  CollectionAvailabilityFilter.prototype.renderPagination = function () {
    if (!this.paginationMount) return;
    injectPaginationStyles();

    var self = this;
    var pageInfo = this.lastPageInfo || {};
    var currentPage = this.pageIndex + 1;
    var html = '';

    if (pageInfo.hasNextPage || currentPage > 1) {
      // Sin desplegable de salto acá a propósito: saltar a una página
      // arbitraria requeriría escanear secuencialmente todas las páginas
      // intermedias (no hay atajo de cursor con acumulación de buffer por
      // medio). Pero si ya se conoce el total real (this.availabilityCounts,
      // calculado por computeAvailabilityCounts), sí se muestra "of N pages"
      // como dato informativo — se corrige solo cuando ese cálculo termina,
      // ver patchHeadingCount, que vuelve a llamar renderPagination().
      var totalPagesLabel = '';
      var paginationCounts = this.currentCounts();
      if (paginationCounts && !paginationCounts.truncated) {
        var total = this.availabilityFilter ? paginationCounts.inStock : paginationCounts.specialOrder;
        totalPagesLabel = ' of ' + Math.max(currentPage, Math.ceil(total / this.pageSize)) + ' pages';
      }
      html = ''
        + '<nav class="hub-pagination" aria-label="Pagination" data-availability-live-pagination>'
        + '  <button type="button" class="hub-pagination__btn" data-page-nav="prev"' + (currentPage > 1 ? '' : ' disabled') + '>Previous</button>'
        + '  <span class="hub-pagination__of">Page ' + currentPage + totalPagesLabel + '</span>'
        + '  <button type="button" class="hub-pagination__btn hub-pagination__btn--accent" data-page-nav="next"' + (pageInfo.hasNextPage ? '' : ' disabled') + '>Next</button>'
        + '</nav>';
    }

    // Reemplaza SOLO el <nav>/.pagination existente (nunca el innerHTML del
    // mount completo) — mismo cuidado que renderPagination en
    // hub-storefront-filters.js, para no destruir nada que viva junto a él.
    var existing = this.paginationMount.querySelector('nav, .pagination');
    if (html) {
      if (existing) {
        existing.outerHTML = html;
      } else {
        this.paginationMount.insertAdjacentHTML('afterbegin', html);
      }
    } else if (existing) {
      existing.remove();
    }

    var nav = this.paginationMount.querySelector('[data-availability-live-pagination]');
    if (!nav) return;

    nav.querySelectorAll('[data-page-nav]').forEach(function (btn) {
      if (btn.disabled) return;
      btn.addEventListener('click', function () {
        // Dos clics rápidos lanzaban dos goLive() concurrentes sobre el
        // MISMO slot de availabilityBufferHistory, pisándose entre sí
        // (reproducido: 2 fetches de productos en paralelo).
        // setLoadingState solo marcaba aria-busy, nunca bloqueaba.
        if (self.liveNavigating) return;
        self.liveNavigating = true;
        self.pageIndex = btn.dataset.pageNav === 'prev'
          ? Math.max(0, self.pageIndex - 1)
          : self.pageIndex + 1;
        var release = function () { self.liveNavigating = false; };
        self.goLive().then(function () {
          release();
          self.scrollGridIntoView();
        }, release);
      });
    });
  };

  CollectionAvailabilityFilter.prototype.scrollGridIntoView = function () {
    if (this.grid && this.grid.scrollIntoView) {
      this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  CollectionAvailabilityFilter.prototype.setLoadingState = function (isLoading) {
    if (this.grid) this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (this.paginationMount) this.paginationMount.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  };

  var instances = [];
  document.querySelectorAll('[data-collection-availability-filter]').forEach(function (root) {
    instances.push(new CollectionAvailabilityFilter(root));
  });

  // Ver handleThemeReload arriba: theme.js dispara "theme:loading:end" al
  // final de CADA _reload() (otro filtro, orden, o paginación nativa), y
  // ese _reload() reemplaza el innerHTML de .collection__dynamic-part —
  // donde viven this.grid/this.paginationMount — dejando huérfanas las
  // referencias que este archivo capturó una sola vez al cargar la página.
  if (instances.length) {
    document.addEventListener('theme:loading:end', function () {
      instances.forEach(function (instance) {
        instance.handleThemeReload();
      });
    });
  }
})();
